<template>
  <div>
    <div class="relative mt-x">
      <h1 class="page-title">{{ $t(`pages.news.type.${ this.type }`) }}</h1>
    </div>

    <div class="__news">
      <ArticlePreview v-for="article of news" :key="article" :article="article"></ArticlePreview>
    </div>

    <div v-if="no_news">NO NEWS</div>

    <div class="row justify--center" style="margin-top: 40px;">
      <va-button v-if="next_page" @click="getNews()">{{ $t('pages.news.more') }}</va-button>
    </div>
  </div>
</template>

<script>
import { defineComponent, computed, reactive } from 'vue'

import { useHead } from '@vueuse/head'

import ArticlePreview from '@/components/article/ArticlePreview.vue';
import cmsService from '@/services/cms.service';

export default defineComponent({
  components: { ArticlePreview },

  setup() {
    const siteData = reactive({
      title: `News - ITF Bruchköbel`,
      description: `Willkommen auf der offiziellen Website des HEAD Cup Bruchköbel operated by Achat Hotels!`,
      thumbnail: '',
      lang: '',
      url: '',
      locale: 'en',
      locale_alternate: 'de',
      ld_json_webpage: '',
      ld_json_website: ''
    })

    useHead({
      title: computed(() => siteData.title),
      htmlAttrs: {
        lang: computed(() => siteData.lang),
      },
      meta: [
        {
          name: `description`,
          content: computed(() => siteData.description),
        },
        // Twitter
        { name: 'twitter:title', content: computed(() => siteData.title) },
        { name: 'twitter:description', content: computed(() => siteData.description) },
        { name: 'twitter:image', content: computed(() => siteData.thumbnail) },
        { name: 'twitter:url', content: computed(() => siteData.url) },
        { name: 'twitter:domain', content: location.origin },
        { name: 'twitter:card', content: 'summary_large_image' },
        // Google+ / Schema.org
        { itemprop: 'name', content: computed(() => siteData.title) },
        { itemprop: 'description', content: computed(() => siteData.description) },
        // Facebook / Open Graph
        { property: 'og:url', content: computed(() => siteData.url) },
        { property: 'og:type', content: 'website' },
        { property: 'og:title', content: computed(() => siteData.title) },
        { property: 'og:description', content: computed(() => siteData.description) },
        { property: 'og:image', content: computed(() => siteData.thumbnail) },

        { property: 'og:locale', content: computed(() => siteData.locale) },
        { property: 'og:locale:alternate', content: computed(() => siteData.locale_alternate) },
      ],
      link: [
        { rel: 'canonical', href: computed(() => siteData.url) , id: 'canonical' },
      ],
      script: [
        {
          type: 'application/ld+json',
          children : computed(() => siteData.ld_json_webpage)
        },
        {
          type: 'application/ld+json',
          children : computed(() => siteData.ld_json_website)
        }
      ]
    })

    return {
      siteData
    }
  },

  data() {
    return {
      type: 'news',
      title: this.$t(`pages.news.type.news`),

      news: [],
      page_size: 9,
      next_page: 1,
      no_news: false
    }
  },

  computed: {},

  methods: {
    getNews () {
      cmsService.getArticlesByFilter(this.next_page, this.page_size, [this.type], [], [this.$i18n.locale])
        .then((data) => {
          this.next_page = data.data.nextPage;
          let _articles = data.data.docs;

          _articles = _articles.map((item) => {
            if('de' in item) {
              item['de'] = JSON.parse(item['de']);
            }

            if('en' in item) {
              item['en'] = JSON.parse(item['en']);
            }

            return item;
          })

          this.no_news = data.data.totalDocs == 0;

          this.news = this.news.concat(_articles);
        }).catch((error) => {
          this.$vaToast.init({ message: 'Error while loading the page!', color: 'danger' });
        });
    },
  },

  mounted() {
    this.type = this.$route.name;
    this.title = this.$t(`pages.news.type.${ this.type }`),

    this.getNews();

    if(this.$i18n.locale == 'de') {
      this.siteData.title = `${ this.title } - ITF Bruchköbel`;
      this.siteData.description = `Willkommen auf der offiziellen Website des HEAD Cup Bruchköbel operated by Achat Hotels!`;
      this.siteData.lang = this.$i18n.locale;
      this.siteData.url = location.origin + '/de/';
    } else {
      this.siteData.title = `${ this.title } - ITF Bruchkoebel`;
      this.siteData.description = `Welcome to the official website of the HEAD Cup Bruchkoebel operated by Achat Hotels!`;
      this.siteData.lang = this.$i18n.locale;
      this.siteData.url = location.origin + '/en/';
    }

    this.siteData.ld_json_webpage = JSON.stringify({
      "@context": "http://schema.org",
      "@type": "WebPage",
      "name": this.$i18n.locale == 'de' ? `ITF Bruchköbel - ITF Head Cup` : `ITF Bruchkoebel - ITF Head Cup`,
      "description": this.$i18n.locale == 'de' ?  'Willkommen auf der offiziellen Website des HEAD Cup Bruchköbel operated by Achat Hotels!' : `Welcome to the official website of the HEAD Cup Bruchkoebel operated by Achat Hotels!`,
    });

    this.siteData.ld_json_website = JSON.stringify({
      "@context": "http://schema.org",
      "@type": "WebSite",
      "name": this.$i18n.locale == 'de' ? `ITF Bruchköbel - ITF Head Cup` : `ITF Bruchkoebel - ITF Head Cup`,
      "url": location.origin + '/' + this.$i18n.locale,
    })

    this.siteData.locale = this.$i18n.locale;
    this.siteData.locale_alternate = this.$i18n.locale == 'de' ? 'en' : 'de';
  },
})
</script>

<style lang="scss" scoped>
  .relative {
    position: relative;

    &:after {
      content: "";
      position: absolute;
      display: block;
      width: 100%;
      height: 0;
      left: 0;
      bottom: 0;
      margin-top: 1px;
      border-bottom: 1px solid #eee;
      z-index: -1;
    }
  }

  .page-title {
    display: inline-block;
    text-transform: uppercase;
    font-size: 28px;
    border-bottom: 2px solid var(--va-primary);

    margin-top: 4px;
  }

  .__news {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;

    margin-top: 40px;
  }
</style>
